import {useState, useEffect} from "react";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@sakura-grp/ui/drawer";
import {Button} from "@sakura-grp/ui/button";
import {Input} from "@sakura-grp/ui/input";
import {Label} from "@sakura-grp/ui/label";
import {Textarea} from "@sakura-grp/ui/textarea";
import {toast} from "@sakura-grp/ui/sonner";
import {
  updateBusinessCard,
  normalizeMobileInput,
  isValidMobileNumber,
  isValidEmailAddress,
  normalizePostalCodeInput,
  isValidPostalCode,
} from "@/lib/api/business-cards";
import {useDirtyDialogGuard} from "@/hooks/use-dirty-dialog-guard";
import {useAddressAutofill} from "@/hooks/useAddressAutofill";
import {useKeyboardInset} from "@/hooks/useKeyboardInset";
import {useKanaField} from "@/hooks/useKanaField";
import {CompanySelectSheet} from "../../shared/company-select-sheet";
import {CompanyRenameDialog} from "../../shared/company-rename-dialog";
import type {BusinessCard} from "~/types/customer-detail.types";

interface BusinessCardEditSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 編集対象の名刺（顧客詳細の transform 済みデータ） */
  card: BusinessCard;
  /** キャッシュ無効化用（顧客詳細クエリの先頭キー） */
  customerId: string;
}

export function BusinessCardEditSheet({
  open,
  onOpenChange,
  card,
  customerId,
}: BusinessCardEditSheetProps) {
  const [name, setName] = useState(card.name);
  const [kana, setKana] = useState(card.kana);
  const [mobile, setMobile] = useState(card.mobile);
  const [email, setEmail] = useState(card.email);
  const [company, setCompany] = useState(card.companyName);
  const [position, setPosition] = useState(card.position);
  const [companyPhone, setCompanyPhone] = useState(card.companyPhone);
  const [postalCode, setPostalCode] = useState(card.postalCode);
  const [address, setAddress] = useState(card.companyAddress);
  const [note, setNote] = useState(card.handwrittenMemo ?? "");
  const [companySheetOpen, setCompanySheetOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const queryClient = useQueryClient();

  // 郵便番号7桁で住所を前置入力（既存住所・手入力済みは上書きしない）
  const {resetAutofill} = useAddressAutofill({postalCode, address, onFill: setAddress});

  const {
    error: kanaError,
    visibleError: kanaVisibleError,
    inputProps: kanaInputProps,
    reset: resetKanaComposition,
  } = useKanaField(kana);

  // ドロワーを開いたとき（または対象カードが変わったとき）に現在値で初期化する。
  // card オブジェクトは毎レンダー新参照になりうるので id をキーにする（編集中の
  // 再フェッチでフォームが巻き戻らないよう、card.id 単位でのみリセット）。
  useEffect(() => {
    if (open) {
      setName(card.name);
      setKana(card.kana);
      setMobile(card.mobile);
      setEmail(card.email);
      setCompany(card.companyName);
      setPosition(card.position);
      setCompanyPhone(card.companyPhone);
      setPostalCode(card.postalCode);
      setAddress(card.companyAddress);
      setNote(card.handwrittenMemo ?? "");
      setCompanySheetOpen(false);
      setRenameOpen(false);
      resetKanaComposition();
      // 既存住所を初期表示するので、その値を「自動入力値」と誤認して上書きしないよう
      // 自動入力の履歴をクリアする（編集対象カードが変わったときも同様）
      resetAutofill();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, card.id, resetKanaComposition, resetAutofill]);

  const mutation = useMutation({
    mutationFn: () =>
      updateBusinessCard(card.id, {
        nameKanji: name,
        nameFurigana: kana,
        mobileNumber: mobile,
        emailAddress: email,
        companyName: company,
        position,
        companyPhone,
        address,
        postalCode,
        handwrittenNote: note,
      }),
    onSuccess: () => {
      toast.success("名刺を更新しました");
      // 詳細（自身）・一覧・件数バッジを再取得（会社名は company_id 再解決で一覧にも反映）
      queryClient.invalidateQueries({queryKey: ["customer-detail", customerId]});
      queryClient.invalidateQueries({queryKey: ["tab-customers"]});
      queryClient.invalidateQueries({queryKey: ["tab-counts"]});
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const normalizedMobile = normalizeMobileInput(mobile);
  const mobileError =
    normalizedMobile.length > 0 && !isValidMobileNumber(normalizedMobile)
      ? "携帯電話番号の形式が正しくありません"
      : null;

  const normalizedPostal = normalizePostalCodeInput(postalCode);
  const postalError =
    postalCode.trim().length > 0 && !isValidPostalCode(normalizedPostal)
      ? "郵便番号は7桁の数字で入力してください"
      : null;

  const emailTrimmed = email.trim();
  const emailError =
    emailTrimmed.length > 0 && !isValidEmailAddress(emailTrimmed)
      ? "メールアドレスの形式が正しくありません"
      : null;

  const hasChanges =
    name !== card.name ||
    kana !== card.kana ||
    mobile !== card.mobile ||
    email !== card.email ||
    company !== card.companyName ||
    position !== card.position ||
    companyPhone !== card.companyPhone ||
    postalCode !== card.postalCode ||
    address !== card.companyAddress ||
    note !== (card.handwrittenMemo ?? "");

  // 必須は氏名・フリガナ。連絡先等は任意だが、入れるなら正しい形式であること。変更がなければ保存不可。
  // フリガナのカタカナ検証は「フリガナを編集したとき」だけ課す（Sansan 由来等で既存値が
  // 非カタカナの名刺でも、無関係な項目だけ直したいケースを保存ブロックしない）。
  const canSave =
    name.trim().length > 0 &&
    kana.trim().length > 0 &&
    (kana === card.kana || !kanaError) &&
    !mobileError &&
    !emailError &&
    !postalError &&
    hasChanges;

  const {DirtyConfirmDialog, requestClose} = useDirtyDialogGuard({
    isDirty: hasChanges,
    onClose: () => onOpenChange(false),
  });

  const keyboardInset = useKeyboardInset();

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        dismissible={!hasChanges}
        repositionInputs={false}
      >
        <DrawerContent
          className="px-lg pb-safe-min h-[85vh]"
          style={keyboardInset > 0 ? {paddingBottom: keyboardInset} : undefined}
        >
          <DrawerHeader className="pb-md">
            <DrawerTitle>名刺を編集</DrawerTitle>
            <DrawerDescription>この名刺の情報を編集します</DrawerDescription>
          </DrawerHeader>

          <div className="space-y-md min-h-0 flex-1 overflow-y-auto">
            <div className="space-y-sm">
              <Label htmlFor="edit-card-name">氏名（必須）</Label>
              <Input
                id="edit-card-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="山田 太郎"
                className="text-base"
              />
            </div>
            <div className="space-y-sm">
              <Label htmlFor="edit-card-kana">フリガナ（必須）</Label>
              <Input
                id="edit-card-kana"
                value={kana}
                onChange={(e) => setKana(e.target.value)}
                placeholder="ヤマダ タロウ"
                className="text-base"
                aria-invalid={!!kanaVisibleError}
                {...kanaInputProps}
              />
              {kanaVisibleError && (
                <p className="text-destructive text-xs" role="alert">
                  {kanaVisibleError}
                </p>
              )}
            </div>
            <div className="space-y-sm">
              <Label htmlFor="edit-card-mobile">携帯電話</Label>
              <Input
                id="edit-card-mobile"
                type="tel"
                inputMode="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="090-1234-5678"
                className="text-base"
                aria-invalid={!!mobileError}
              />
              {mobileError && (
                <p className="text-destructive text-xs" role="alert">
                  {mobileError}
                </p>
              )}
            </div>
            <div className="space-y-sm">
              <Label htmlFor="edit-card-email">メールアドレス</Label>
              <Input
                id="edit-card-email"
                type="email"
                inputMode="email"
                autoCapitalize="none"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="taro@example.com"
                className="text-base"
                aria-invalid={!!emailError}
              />
              {emailError && (
                <p className="text-destructive text-xs" role="alert">
                  {emailError}
                </p>
              )}
            </div>
            <div className="space-y-sm">
              <Label htmlFor="edit-card-company">会社名</Label>
              <button
                id="edit-card-company"
                type="button"
                onClick={() => setCompanySheetOpen(true)}
                className="border-input bg-input/10 h-11 gap-sm flex w-full items-center justify-between rounded-md border px-3 text-base"
              >
                <span className={company ? "" : "text-muted-foreground/50"}>
                  {company || "会社を選ぶ（任意）"}
                </span>
                <span className="text-muted-foreground shrink-0">›</span>
              </button>
              {company !== card.companyName ? (
                <p className="text-muted-foreground text-xs">
                  この名刺だけ会社が変わります（保存で確定。元の会社は変わりません）
                </p>
              ) : (
                card.companyName.trim().length > 0 && (
                  <button
                    type="button"
                    onClick={() => setRenameOpen(true)}
                    className="text-muted-foreground text-xs underline underline-offset-2"
                  >
                    会社名のまちがいを全体修正する
                  </button>
                )
              )}
            </div>
            <div className="space-y-sm">
              <Label htmlFor="edit-card-position">役職</Label>
              <Input
                id="edit-card-position"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                placeholder="営業部長"
                className="text-base"
              />
            </div>
            <div className="space-y-sm">
              <Label htmlFor="edit-card-company-phone">会社電話</Label>
              <Input
                id="edit-card-company-phone"
                type="tel"
                inputMode="tel"
                value={companyPhone}
                onChange={(e) => setCompanyPhone(e.target.value)}
                placeholder="03-1234-5678"
                className="text-base"
              />
            </div>
            <div className="space-y-sm">
              <Label htmlFor="edit-card-postal">郵便番号</Label>
              <Input
                id="edit-card-postal"
                inputMode="numeric"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder="1040061"
                className="text-base"
                aria-invalid={!!postalError}
              />
              {postalError && (
                <p className="text-destructive text-xs" role="alert">
                  {postalError}
                </p>
              )}
            </div>
            <div className="space-y-sm">
              <Label htmlFor="edit-card-address">住所</Label>
              <Input
                id="edit-card-address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="東京都中央区銀座1-2-3 サクラビル5F"
                className="text-base"
              />
            </div>
            <div className="space-y-sm">
              <Label htmlFor="edit-card-note">手書きメモ</Label>
              <Textarea
                id="edit-card-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="名刺に書き込んだメモなど"
                rows={3}
                className="resize-none text-base"
              />
            </div>
          </div>

          <DrawerFooter className="p-0 pt-md flex shrink-0 flex-col gap-md">
            <Button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || !canSave}
            >
              {mutation.isPending ? "保存中..." : "保存する"}
            </Button>
            <Button
              variant="outline"
              onClick={requestClose}
              disabled={mutation.isPending}
            >
              やめる
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <CompanySelectSheet
        open={companySheetOpen}
        onOpenChange={setCompanySheetOpen}
        value={company}
        onSelect={setCompany}
      />
      <CompanyRenameDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        cardId={card.id}
        currentName={card.companyName}
        onRenamed={() => onOpenChange(false)}
      />
      <DirtyConfirmDialog />
    </>
  );
}
